import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Upload, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Card, Field, TextInput, TextArea } from "./kit";

const LEVELS = [
  { key: "founder", label: "Founder" },
  { key: "ops", label: "Operations Lead" },
  { key: "recruit", label: "Recruitment Lead" },
  { key: "team_lead", label: "Team Lead" },
  { key: "specialist", label: "Specialist" },
];

export default function TeamManager() {
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/team").then(({ data }) => setMembers(data));
  useEffect(() => { load(); }, []);

  const blank = { name: "", role: "", level: "specialist", bio: "", responsibilities: [], photo_url: "" };

  const save = async (m) => {
    if (m.id) {
      await api.put(`/admin/team/${m.id}`, m);
    } else {
      await api.post("/admin/team", m);
    }
    toast.success("Team updated — live on the site now");
    setEditing(null);
    load();
  };

  const remove = async (m) => {
    await api.delete(`/admin/team/${m.id}`);
    toast.success("Member removed");
    load();
  };

  const move = async (m, dir) => {
    const idx = members.findIndex((x) => x.id === m.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= members.length) return;
    const next = [...members];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setMembers(next);
    await api.post("/admin/team/reorder", { ids: next.map((x) => x.id) });
    load();
  };

  const upload = async (m, file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setEditing({ ...m, photo_url: data.url });
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-team-manager">
      <Card title="Team Manager" desc="Add, edit, remove and reorder team profiles. These appear in the org chart on the site.">
        <div className="space-y-3">
          {members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0A0F2C]/40 p-3" data-testid={`team-row-${m.id}`}>
              <img src={m.photo_url} alt="" className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-white text-sm truncate">{m.name}</div>
                <div className="text-xs text-slate-400">{m.role} · {LEVELS.find((l) => l.key === m.level)?.label || m.level}</div>
              </div>
              <button onClick={() => move(m, -1)} className="p-1.5 text-slate-500 hover:text-cyan-300 transition-colors" aria-label="Move up"><ArrowUp size={15} /></button>
              <button onClick={() => move(m, 1)} className="p-1.5 text-slate-500 hover:text-cyan-300 transition-colors" aria-label="Move down"><ArrowDown size={15} /></button>
              <button onClick={() => setEditing({ ...m })} className="text-xs font-semibold text-cyan-400 hover:text-cyan-300 px-2 transition-colors" data-testid={`team-edit-${m.id}`}>Edit</button>
              <button onClick={() => remove(m)} className="p-1.5 text-slate-500 hover:text-red-400 transition-colors" aria-label="Remove member" data-testid={`team-delete-${m.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        <button className="btn btn-sm btn-outline-light mt-5" onClick={() => setEditing({ ...blank })} data-testid="team-add-button">+ Add team member</button>
      </Card>

      {editing && (
        <Card title={editing.id ? "Edit member" : "New member"}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name"><TextInput value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
            <Field label="Role"><TextInput value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} /></Field>
            <Field label="Org chart level">
              <select className="admin-input" value={editing.level} onChange={(e) => setEditing({ ...editing, level: e.target.value })} data-testid="team-level-select">
                {LEVELS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
              </select>
            </Field>
            <Field label="Photo">
              <div className="flex items-center gap-3">
                <button className="btn btn-sm btn-outline-light flex-shrink-0" onClick={() => fileRef.current?.click()} data-testid="team-photo-upload">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload photo
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(editing, e.target.files[0])} />
                {editing.photo_url && <img src={editing.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />}
              </div>
            </Field>
            <Field label="Bio" className="sm:col-span-2"><TextArea rows={3} value={editing.bio} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} /></Field>
            <Field label="Key responsibilities (one per line)" className="sm:col-span-2">
              <TextArea rows={3} value={(editing.responsibilities || []).join("\n")} onChange={(e) => setEditing({ ...editing, responsibilities: e.target.value.split("\n").filter(Boolean) })} />
            </Field>
          </div>
          <div className="flex gap-3 mt-6">
            <button className="btn btn-sm btn-primary" onClick={() => save(editing)} data-testid="team-save-button">Save & Go Live</button>
            <button className="btn btn-sm btn-outline-light" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </Card>
      )}
    </div>
  );
}
